import { isRegexMatch } from "@sscs/utils";
import { AppDispatch } from "Store/store";
import {
  FileStreamProgressMessage,
  FilestreamFileMessage,
  PortalFile,
} from "Types";
import {
  getCurrentUserEmail,
  getWebsocketProtocolHost,
} from "../../../services";
import { fileCreated, fileDeleted } from "../store/files";
import { FileStreamQuery, PortalFiles } from "../types";

const isFilterMatch = (arg: FileStreamQuery, file: PortalFile): boolean => {
  const { filter } = arg;

  if (filter.user && filter.user !== file.user?.email) {
    return false;
  } else if (
    filter.approval_status !== "ALL" &&
    filter.approval_status !== file.approval_status
  ) {
    return false;
  } else if (
    filter.file_name &&
    !isRegexMatch(file.file_name, filter.file_name)
  ) {
    return false;
  } else if (
    filter.deleted_in_days !== null &&
    file.days_until_deletion !== null &&
    file.days_until_deletion !== undefined &&
    filter.deleted_in_days < file.days_until_deletion
  ) {
    return false;
  } else {
    return true;
  }
};

export const updateGetFilesCache = (
  state: unknown,
  dispatch: AppDispatch,
  draft: PortalFiles,
  message: FilestreamFileMessage,
  arg: FileStreamQuery,
): PortalFiles => {
  const currentUser = getCurrentUserEmail(state);

  if (message.action === "updated") {
    const updatedFiles = message.data.filter((updated) =>
      draft.files.some((file) => file.id === updated.id),
    );

    if (updatedFiles.length === 0) return draft;

    // Update files
    const mergedFiles = draft.files.map((file) => {
      const updatedFile = updatedFiles.find(
        (updated) => updated.id === file.id,
      );

      return updatedFile !== undefined ? updatedFile : file;
    });

    // Filter files to keep UI consistent
    const files = mergedFiles.filter((file) => isFilterMatch(arg, file));
    const droppedCount = mergedFiles.length - files.length;

    return {
      match_count: draft.match_count - droppedCount,
      total_count: draft.total_count,
      files,
      deleted_soon_count: draft.deleted_soon_count,
    };
  } else if (message.action === "created") {
    if (
      message.initiated_by &&
      currentUser &&
      message.initiated_by !== currentUser
    ) {
      const createdFiles = message.data
        .filter((file) => isFilterMatch(arg, file))
        .map((file) => file.id);

      if (createdFiles.length > 0) dispatch(fileCreated(createdFiles));
    }
  } else if (message.action === "deleted") {
    if (
      message.initiated_by &&
      currentUser &&
      message.initiated_by !== currentUser
    ) {
      dispatch(fileDeleted(message.keys));
    }
  }

  return draft;
};

export const listenToProgressUpdates = async (
  cacheDataLoaded: Promise<unknown>,
  cacheEntryRemoved: Promise<unknown>,
  groupId: string,
  listener: (event: MessageEvent) => void,
) => {
  const progressUpdates = new WebSocket(
    `${getWebsocketProtocolHost()}/websocket/filestream/${groupId}/progress/`,
  );

  try {
    await cacheDataLoaded;

    progressUpdates.addEventListener("message", listener);
  } catch {
    // no-op in case `cacheEntryRemoved` resolves before `cacheDataLoaded`,
    // in which case `cacheDataLoaded` will throw
  }

  await cacheEntryRemoved;
  progressUpdates.removeEventListener("message", listener);
  progressUpdates.close();
};

export const updateProgress = (
  draft: PortalFiles,
  message: FileStreamProgressMessage,
): PortalFiles => {
  if (message.action !== "updated") {
    return draft;
  }

  return {
    deleted_soon_count: draft.deleted_soon_count,
    match_count: draft.match_count,
    total_count: draft.total_count,
    files: draft.files.map((file) => {
      const updatedFile = message.data.find(
        (updatedFile) => updatedFile.file_id === file.id,
      );

      if (updatedFile) {
        return { ...file, current_status: updatedFile.current_status };
      } else {
        return file;
      }
    }),
  };
};
